import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useCarContext } from "../../context/CarContext";

interface CarDetailsPageProps {
  carId: number;
  lang: string;
}

const CarDetailsPage: React.FC<CarDetailsPageProps> = ({ carId, lang }) => {
  const { cars } = useCarContext();
  const { t } = useTranslation();

  // Trouver la voiture actuelle en fonction de l'ID
  const car = cars.find((c: { id: number }) => c.id === carId);
  if (!car) return null; // Si la voiture n'existe pas, on ne renvoie rien

  // Récupérer la traduction correcte selon la langue
  const carTranslation =
    car.translations[lang as keyof typeof car.translations];

  return (
    <Helmet>
      {/*  Titre optimisé pour le SEO */}
      <title>{`${car.title} - ${car.subtitle} | Race Motors Group`}</title>
      <meta
        name="description"
        content={t("carDetails.metaDescription", { carTitle: car.title })}
      />
      <meta charSet="UTF-8" />
      <meta httpEquiv="Content-Language" content={lang} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />

      {/* Canonical URL pour éviter le contenu dupliqué */}
      <link
        rel="canonical"
        href={`https://www.racemotorsgroup.ch/${lang}/cars/${carId}`}
      />

      {/*  Open Graph (Facebook, LinkedIn, etc.) */}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={`${car.title} - ${car.subtitle}`} />
      <meta property="og:description" content={carTranslation.description} />
      <meta property="og:image" content={car.imageUrl} />
      <meta
        property="og:url"
        content={`https://www.racemotorsgroup.ch/${lang}/cars/${carId}`}
      />
      <meta property="og:site_name" content="Race Motors Group" />
      <meta property="og:locale" content={lang} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${car.title} - ${car.subtitle}`} />
      <meta name="twitter:description" content={carTranslation.description} />
      <meta name="twitter:image" content={car.imageUrl} />
      <meta name="twitter:site" content="@RaceMotorsGroup" />

      {/*  Balises Keywords (moins utilisées, mais utiles) */}
      <meta
        name="keywords"
        content={`${car.title}, ${car.year}, ${car.subtitle}, ${carTranslation.price}, luxury car, sport car`}
      />

      {/* Structured Data (JSON-LD) pour Google */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Car",
          name: car.title,
          brand: {
            "@type": "Brand",
            name: car.subtitle,
          },
          model: car.subtitle,
          url: `https://www.racemotorsgroup.ch/${lang}/cars/${carId}`,
          image: car.imageUrl,
          description: carTranslation.description,
          offers: {
            "@type": "Offer",
            priceCurrency: "EUR",
            price: carTranslation.price,
          },
          vehicleModelDate: car.year,
          driveType: carTranslation.driveType,
          vehicleInteriorColor: carTranslation.interiorColor,
          vehicleExteriorColor: carTranslation.exteriorColor,
        })}
      </script>
    </Helmet>
  );
};

export default CarDetailsPage;
